/**
 * Firebase Error Wrapper Utility
 * Wraps Firebase operations to ensure errors are properly caught and displayed
 * Especially handles offline/network errors
 */

import errorNotificationService from '../services/ErrorNotificationService';

/**
 * Check if device is offline
 * @returns {boolean}
 */
export const isOffline = () => {
  if (typeof navigator !== 'undefined') {
    return !navigator.onLine;
  }
  return false;
};

/**
 * Check if error is a network/offline error
 * @param {Error} error - The error object
 * @returns {boolean}
 */
export const isNetworkError = (error) => {
  if (!error) return false;
  
  const errorCode = error.code || '';
  const errorMessage = (error.message || '').toLowerCase();
  
  // Check for Firebase offline/unavailable errors
  if (errorCode === 'unavailable' || 
      errorCode === 'failed-precondition' ||
      errorCode === 'deadline-exceeded') {
    return true;
  }
  
  // Check for network-related keywords
  if (errorCode.includes('network') || 
      errorMessage.includes('network') ||
      errorMessage.includes('offline') ||
      errorMessage.includes('unavailable') ||
      errorMessage.includes('fetch failed') ||
      errorMessage.includes('connection')) {
    return true;
  }
  
  // Check if device is actually offline
  if (isOffline()) {
    return true;
  }
  
  return false;
};

/**
 * Wrap a Firebase operation to ensure errors are caught and displayed
 * @param {Function} operation - The Firebase operation function
 * @param {Object} options - Options for error handling
 * @param {string} options.context - Context where error occurred
 * @param {string} options.operationName - Name of the operation (e.g., "Update Invoice")
 * @param {boolean} options.showNotification - Whether to show notification (default: true)
 * @returns {Promise} The result of the operation
 */
export const wrapFirebaseOperation = async (operation, options = {}) => {
  const {
    context = 'FirebaseOperation',
    operationName = 'Operation',
    showNotification = true
  } = options;
  
  try {
    // Check if offline before attempting operation
    if (isOffline()) {
      const error = new Error('You are currently offline. Please check your internet connection and try again.');
      error.code = 'OFFLINE';
      error.isOffline = true;
      
      if (showNotification) {
        errorNotificationService.handleNetworkError(error, {
          context,
          title: 'Offline',
          showNotification: true
        });
      }
      
      throw error;
    }
    
    // Execute the operation
    return await operation();
    
  } catch (error) {
    // Check if it's a network error
    if (isNetworkError(error)) {
      // Enhance error with offline context if needed
      if (isOffline() && !error.isOffline) {
        error.code = error.code || 'OFFLINE';
        error.message = 'You are currently offline. Please check your internet connection and try again.';
      }
      
      if (showNotification) {
        errorNotificationService.handleNetworkError(error, {
          context,
          title: isOffline() ? 'Offline' : 'Network Error',
          showNotification: true
        });
      }
    } else {
      // Handle other Firebase errors
      if (showNotification) {
        errorNotificationService.handleFirebaseError(error, {
          context,
          title: `${operationName} Failed`,
          showNotification: true
        });
      }
    }
    
    // Re-throw error so calling code can handle it
    throw error;
  }
};

/**
 * Wrap multiple Firebase operations in a batch
 * @param {Function} batchOperation - Function that returns a batch operation
 * @param {Object} options - Options for error handling
 * @returns {Promise} The result of the batch operation
 */
export const wrapFirebaseBatch = async (batchOperation, options = {}) => {
  return wrapFirebaseOperation(
    () => batchOperation(),
    {
      ...options,
      operationName: options.operationName || 'Batch Operation'
    }
  );
};

export default {
  isOffline,
  isNetworkError,
  wrapFirebaseOperation,
  wrapFirebaseBatch
};















